import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldCheck, Inbox } from "lucide-react";
import api, { formatCurrency, COUNTRY_FLAGS } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "just now";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ProofOfPayments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/public/recent-payouts")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="proof-page">
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold">Proof of Payments</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#0B3C5D] mt-3 tracking-tight">
            Recent completed payouts.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Every entry below is a real transaction that completed on Giftopay. Personal data is
            masked for seller privacy.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <p className="text-center text-sm text-slate-500" data-testid="proof-loading">Loading recent payouts…</p>
          ) : items.length === 0 ? (
            <div className="max-w-md mx-auto text-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10" data-testid="proof-empty">
              <div className="w-14 h-14 rounded-full bg-white border border-slate-200 flex items-center justify-center mx-auto">
                <Inbox className="w-6 h-6 text-slate-400" />
              </div>
              <h2 className="font-display text-xl font-bold text-[#0B3C5D] mt-4">No completed payouts yet</h2>
              <p className="mt-2 text-sm text-slate-600">
                As soon as transactions are completed, real, verified payouts will appear here.
                We don&apos;t show placeholder or stock testimonials.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((p, i) => (
                <motion.div
                  key={p.tx_id || i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                  className="rounded-2xl border border-slate-200 bg-white p-5 card-hover"
                  data-testid={`proof-card-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#00C853]/10 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-[#00C853]" />
                      </div>
                      <div>
                        <p className="font-display font-bold text-[#0B3C5D] text-sm">
                          {p.masked_seller} {COUNTRY_FLAGS[p.country]}
                        </p>
                        <p className="text-[11px] text-slate-500">{timeAgo(p.completed_at)}</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-semibold text-[#00C853] bg-[#00C853]/10 px-2 py-1 rounded-full">Paid</span>
                  </div>
                  <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">{p.brand_name}</p>
                    <p className="font-display text-xl font-bold text-[#0B3C5D] mt-0.5">
                      {formatCurrency(p.final_payout, p.currency)}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">via {p.payout_method_name}</p>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#00C853]" /> Verified · {p.tx_id}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
